import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Star, Quote, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch {
      // Silent fail - show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  return (
    <div className="min-h-screen pt-20" data-testid="testimonials-page">
      <Helmet>
        <title>Avis et Témoignages | Cours de Musique à Périgueux | École de Musique Pour Tous</title>
        <meta name="description" content="Découvrez les avis des élèves et parents sur nos cours de piano, guitare, chant et éveil musical à Périgueux. 100% de satisfaction !" />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/avis" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-semibold mb-4">
              Avis Cours de Musique Périgueux
            </span>
            <h1 className="font-dancing text-5xl sm:text-6xl text-burgundy mb-6">
              Témoignages de nos élèves à Périgueux
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto font-nunito">
              Découvrez les retours d'expérience des élèves et de leurs parents sur nos cours de piano, guitare, chant et éveil musical. 
              Leur satisfaction est notre plus belle récompense.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-burgundy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-dancing text-4xl mb-1">100%</p>
              <p className="text-white/70 text-sm font-nunito">Recommandation</p>
            </div>
            <div>
              <p className="font-dancing text-4xl mb-1">5/5</p>
              <p className="text-white/70 text-sm font-nunito">Note moyenne</p>
            </div>
            <div>
              <p className="font-dancing text-4xl mb-1">+50</p>
              <p className="text-white/70 text-sm font-nunito">Élèves accompagnés</p>
            </div>
            <div>
              <p className="font-dancing text-4xl mb-1">10+</p>
              <p className="text-white/70 text-sm font-nunito">Années d'expérience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-burgundy border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-3xl shadow-sm p-8 relative"
                  data-testid={`testimonial-card-${index}`}
                >
                  {/* Quote Icon */}
                  <div className="absolute -top-4 left-8 w-10 h-10 bg-coral rounded-full flex items-center justify-center shadow">
                    <Quote className="w-5 h-5 text-white" />
                  </div>

                  {/* Stars */}
                  <div className="flex gap-1 mb-4 pt-2">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} className="w-5 h-5 fill-coral text-coral" />
                    ))}
                  </div>

                  {/* Content */}
                  <p className="text-gray-700 leading-relaxed mb-6 italic font-nunito">
                    "{testimonial.content}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-burgundy/10 rounded-full flex items-center justify-center">
                      <span className="text-burgundy font-bold">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-nunito font-bold text-burgundy">{testimonial.author}</p>
                      <p className="text-gray-500 text-sm">Avis vérifié</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Google Reviews Note */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 font-nunito">
            Ces avis sont issus de témoignages vérifiés de parents et d'élèves. 
            Vous pouvez également consulter mes avis sur{' '}
            <a 
              href="https://eveil-musical-et-sensoriel-charlotte-naboulet.business.site/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-burgundy underline hover:no-underline"
            >
              Google Business
            </a>.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cream via-white to-coral/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-dancing text-4xl sm:text-5xl text-burgundy mb-6">
              Rejoignez nos élèves satisfaits
            </h2>
            <p className="text-gray-600 text-lg mb-8 font-nunito">
              Réservez votre cours d'essai gratuit et découvrez par vous-même 
              la méthode qui fait la différence.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-burgundy text-white rounded-full font-bold hover:bg-burgundy-600 transition-all"
                data-testid="testimonials-cta"
              >
                Réserver mon essai gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/cours"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-burgundy text-burgundy rounded-full font-bold hover:bg-burgundy hover:text-white transition-all"
                data-testid="testimonials-courses-link"
              >
                Découvrir les cours
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
